import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/layout/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { columns, columnMap, site } from '@/mocks/columns';

export default function ColumnDetail() {
  const { slug = '' } = useParams();
  const col = columnMap[slug];

  if (!col) {
    return (
      <div className="min-h-screen bg-background-50">
        <Seo
          title="栏目未找到 - 看料社"
          description="该栏目不存在或已被调整。返回看料社栏目总览，查看更多深扒与专栏分析内容。"
          canonical={`${site.domain}/lanmu`}
        />
        <Header />
        <main className="mx-auto flex max-w-6xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="font-heading text-6xl font-black text-background-200">404</span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这个栏目</h1>
          <p className="mt-2 text-sm text-foreground-600">这个栏目可能已经下线或被合并。去栏目总览看看吧。</p>
          <Link
            to="/lanmu"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700"
          >
            返回栏目总览
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const list = articles.filter((a) => a.column === col.slug);
  const others = columns.filter((c) => c.slug !== col.slug);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${col.name} - 看料社`,
      url: `${site.domain}/lanmu/${col.slug}`,
      inLanguage: 'zh-CN',
      description: col.desc,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: site.domain },
        { '@type': 'ListItem', position: 2, name: '看料栏目', item: `${site.domain}/lanmu` },
        { '@type': 'ListItem', position: 3, name: col.name, item: `${site.domain}/lanmu/${col.slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title={`${col.name} · 看料社${col.name}深扒与专栏分析`}
        description={`看料社「${col.name}」栏目：${col.desc} 汇集${col.name}下的小编深扒、深度扒文与专栏分析，帮你理清事件脉络。`}
        keywords={`看料社${col.name},${col.name},小编深扒,专栏分析,深度扒文`}
        canonical={`${site.domain}/lanmu/${col.slug}`}
        jsonLd={jsonLd}
      />
      <Header />

      <main>
        {/* 栏目头图 */}
        <section className="relative">
          <img
            src={col.cover}
            alt={`${col.name}栏目头图`}
            title={`看料社${col.name}栏目`}
            className="h-64 w-full object-cover object-top md:h-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/20" />
          <div className="absolute inset-x-0 bottom-0">
            <div className="mx-auto max-w-6xl px-4 pb-8 md:px-6">
              <nav className="text-xs text-background-200/90" aria-label="面包屑">
                <ol className="flex flex-wrap items-center gap-1">
                  <li>
                    <Link to="/" className="transition-colors hover:text-background-50">首页</Link>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li>
                    <Link to="/lanmu" className="transition-colors hover:text-background-50">看料栏目</Link>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li className="text-accent-200" aria-current="page">{col.name}</li>
                </ol>
              </nav>
              <div className="mt-3 flex items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-md bg-primary-600 font-heading text-xl font-black text-background-50">
                  {col.mark}
                </span>
                <div>
                  <h1 className="font-heading text-2xl font-black text-background-50 md:text-3xl">
                    {col.name}
                    <span className="ml-3 align-middle text-xs font-normal uppercase tracking-[0.25em] text-background-200">
                      {col.en}
                    </span>
                  </h1>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-100/90">
                    {col.desc}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 文章列表 */}
        <section className="border-b border-background-200">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {col.name}·全部文章
                <span className="ml-2 text-sm font-normal text-foreground-500">{list.length} 篇</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-5">
              {list.map((a) => (
                <ArticleCard key={a.slug} article={a} layout="horizontal" />
              ))}
            </div>

            {list.length === 0 && (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
                <p className="text-sm text-foreground-600">该栏目正在筹备首批内容，敬请期待。</p>
              </div>
            )}
          </div>
        </section>

        {/* 其他栏目 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-14">
            <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">探索其他栏目</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {others.map((c) => (
                <Link
                  key={c.slug}
                  to={`/lanmu/${c.slug}`}
                  className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-background-100 font-heading text-sm font-black uppercase text-primary-600">
                    {c.mark}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                      {c.name}
                    </span>
                    <span className="block truncate text-xs text-foreground-500">{c.en}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}